import React from 'react';
import { MonitorUp, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { ScreenShareStatus } from './lib/use-screen-share';
import type { RemoteTool } from './lib/remote-share-context';

const POSTURE_NOW = [
  'Ask the client to stand still, whole body in frame.',
  'Mirror if left and right look swapped. Crop if the window shows call buttons or your own video.',
  'Pick a view, then Capture pose. Review & save opens a new tab — keep this one open.',
];

const SOP_NOW = [
  'Pick a test and ask the client to hold still.',
  'Freeze frame, line up the angle, then Apply. Save the active assessment.',
];

function RoleList({ title, items }: { title: string; items: string[] }) {
  return (
    <div className="rounded-lg border bg-card p-3">
      <h3 className="text-sm font-semibold">{title}</h3>
      <ul className="mt-2 list-disc space-y-1 pl-4 text-sm text-muted-foreground">
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Shown before a call window is shared: join the call first, then share that window,
 * then a preview of what the practitioner and the client do next.
 */
export function RemoteShareSetup({
  status,
  onShare,
}: {
  status: ScreenShareStatus;
  onShare: () => void;
}) {
  return (
    <div className="flex w-full max-w-3xl flex-col gap-6 pb-6">
      <ol className="list-decimal space-y-6 pl-5 marker:text-base marker:font-semibold marker:text-primary">
        <li className="pl-2">
          <h2 className="text-base font-semibold">Join a video call with the client</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Hop on Zoom, Microsoft Teams, Google Meet, or a similar call. The client stays on that call
            and does not open Harry.
          </p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <RoleList
              title="You"
              items={[
                'Run the call and Harry on this computer.',
                'Tell them when to stand still, turn, or hold a movement.',
              ]}
            />
            <RoleList
              title="The client"
              items={[
                'Join the call and turn the camera on.',
                'For posture, stand back so their whole body is in frame.',
                'Follow your instructions.',
              ]}
            />
          </div>
        </li>

        <li className="pl-2">
          <h2 className="text-base font-semibold">Share the call window</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            When the browser asks what to share, choose the call window (Zoom, Teams, or Meet), not this
            Harry page. You only share once.
          </p>
          <div className="mt-4 flex flex-col items-start gap-3">
            {status === 'error' && (
              <Alert variant="destructive">
                <AlertDescription>Could not start screen sharing. Try again.</AlertDescription>
              </Alert>
            )}
            {status === 'ended' && (
              <p className="text-sm text-muted-foreground">
                Screen sharing stopped. Share the call window again to continue. This page is still here.
              </p>
            )}
            <Button size="lg" disabled={status === 'requesting'} aria-busy={status === 'requesting'} onClick={onShare}>
              <MonitorUp className="mr-2 h-5 w-5" />
              {status === 'requesting'
                ? 'Requesting screen share…'
                : status === 'ended'
                  ? 'Share again'
                  : 'Share call window'}
            </Button>
          </div>
        </li>
      </ol>

      <section className="rounded-lg border bg-muted/40 p-4">
        <h2 className="text-base font-semibold">After the window is shared</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Switch between the two assessments at the top. You do not share again.
        </p>
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          <div>
            <h3 className="text-sm font-semibold">Posture Analysis</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Landmarks appear on the client. Capture front, back, left, and right, then Review &amp; save
              in a new tab. Keep this tab open.
            </p>
          </div>
          <div>
            <h3 className="text-sm font-semibold">SOP Assessment</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Ask them to hold still. Freeze frame, line up the angle, Apply, then save.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

/** Short, mode-specific steps once the call window is on screen. */
/** `onClose` turns the steps off (Settings > Tooltips & Help); the top-bar ? brings them back. */
export function RemoteLiveGuide({ tool, onClose }: { tool: RemoteTool; onClose?: () => void }) {
  const posture = tool === 'landmarks';
  const steps = posture ? POSTURE_NOW : SOP_NOW;
  return (
    <section className="shrink-0 rounded-lg border bg-muted/40 px-4 py-3" aria-label="What to do now">
      <div className="flex items-start justify-between gap-3">
        <h2 className="text-sm font-semibold">
          {posture ? 'Posture Analysis — what to do now' : 'SOP Assessment — what to do now'}
        </h2>
        <div className="flex shrink-0 items-center gap-1">
          {onClose && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              aria-label="Close steps"
              title="Close steps (bring them back with the ? button or in Settings)"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
      <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
        {steps.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
    </section>
  );
}
